/**
 * The orthophoto report's CHM quality: `chm_quality.json` beside the photogrammetry's CHM
 * (forestum `chm_quality`, odm from forun's version 11). How far the CHM's bare ground is from
 * zero — the photogrammetry's vertical shift, positive when tree heights come out too high —
 * and whether one constant or one plane takes it out, or the model is warped.
 *
 * The input reaches the page only where the viewer may see it (staff, as a rule): a client's
 * report has no `chm_quality` input at all, and the page shows nothing of it.
 */
import { useEffect, useState } from 'react';
import type { LinkSource } from '@/map/linkSource';

export const CHM_QUALITY_FILE = 'chm_quality.json';

export const VERDICTS = ['constant', 'plane', 'warped', 'insufficient_ground', 'unavailable'] as const;
export type Verdict = (typeof VERDICTS)[number];

/** The limits forestum judged the verdict by, as the report carries them (`thresholds`); a limit the report
 * does not name is null and the page shows no limit for it. forint keeps no limits of its own. */
export interface Limits {
  /** Spread of the bare ground around the offset (or the plane) one correction still fixes. */
  madM: number | null;
  /** A square whose ground median is further than this from the correction = the model is warped. */
  tileResidualM: number | null;
  /** Bare-ground pixels (1 m) a square needs before its median counts. */
  tileGroundPixels: number | null;
  /** Counted squares a tilt (a plane) needs. */
  planeTiles: number | null;
  /** Bare ground in all below which nothing is measured. */
  groundPixels: number | null;
}

/** chm_quality.json's `thresholds` names, by the page's. */
const THRESHOLD_KEYS: Record<keyof Limits, string> = {
  madM: 'max_mad_m',
  tileResidualM: 'max_tile_residual_m',
  tileGroundPixels: 'min_tile_ground_pixels',
  planeTiles: 'min_plane_tiles',
  groundPixels: 'min_ground_pixels',
};

function limits(raw: unknown): Limits {
  const given = isObject(raw) ? raw : {};
  const value = (key: keyof Limits) => {
    const v = num(given[THRESHOLD_KEYS[key]]);
    return v !== null && v > 0 ? v : null;
  };
  return {
    madM: value('madM'),
    tileResidualM: value('tileResidualM'),
    tileGroundPixels: value('tileGroundPixels'),
    planeTiles: value('planeTiles'),
    groundPixels: value('groundPixels'),
  };
}

/** One square of the grid the model is checked on: its bare ground and that ground's median CHM. */
export interface ChmTile {
  x: number;
  y: number;
  groundPixels: number;
  medianM: number;
}

export interface ChmCorrection {
  kind: 'constant' | 'plane';
  offsetM: number;
}

export interface ChmQuality {
  verdict: Verdict;
  /** forestum's own sentence, in English: the figures behind the verdict, or what went wrong. */
  reason: string;
  offsetM: number | null;
  madM: number | null;
  /** The 200 m squares with enough bare ground to measure, and the square size. */
  groundTiles: number | null;
  tileM: number | null;
  tiltXPer100M: number | null;
  tiltYPer100M: number | null;
  /** The worst square's median against the correction. */
  tileResidualMaxM: number | null;
  correction: ChmCorrection | null;
  epsg: number | null;
  /** The CHM's area and its bare ground (1 m pixels = m²) and that ground's share of it. */
  areaHa: number | null;
  groundPixels: number | null;
  groundShare: number | null;
  /** The fullest 0.2 m bin of the bare ground: below the median where vegetation grew since the national survey. */
  modeM: number | null;
  /** The spread around the fitted plane, next to `madM` around the offset. */
  planeMadM: number | null;
  tiles: ChmTile[];
  /** The limits the verdict was judged by, from the report. */
  limits: Limits;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

function tile(raw: unknown): ChmTile[] {
  if (!isObject(raw)) return [];
  const [x, y, groundPixels, medianM] = [num(raw.x), num(raw.y), num(raw.ground_pixels), num(raw.median_m)];
  return x === null || y === null || groundPixels === null || medianM === null ? [] : [{ x, y, groundPixels, medianM }];
}

/** Read leniently; null when the file is not a CHM quality (no verdict forint knows). */
export function chmQuality(raw: unknown): ChmQuality | null {
  if (!isObject(raw) || !VERDICTS.includes(raw.verdict as Verdict)) return null;
  const c = isObject(raw.correction) ? raw.correction : null;
  const correctionKind = c?.kind === 'constant' || c?.kind === 'plane' ? c.kind : null;
  const correctionOffset = c ? num(c.offset_m) : null;
  return {
    verdict: raw.verdict as Verdict,
    reason: typeof raw.reason === 'string' ? raw.reason : '',
    offsetM: num(raw.offset_m),
    madM: num(raw.mad_m),
    groundTiles: num(raw.ground_tiles),
    tileM: num(raw.tile_m),
    tiltXPer100M: num(raw.tilt_x_m_per_100m),
    tiltYPer100M: num(raw.tilt_y_m_per_100m),
    tileResidualMaxM: num(raw.tile_residual_max_m),
    correction: correctionKind && correctionOffset !== null ? { kind: correctionKind, offsetM: correctionOffset } : null,
    epsg: num(raw.epsg),
    areaHa: num(raw.area_ha),
    groundPixels: num(raw.ground_pixels),
    groundShare: num(raw.ground_share),
    modeM: num(raw.mode_m),
    planeMadM: num(raw.plane_mad_m),
    tiles: Array.isArray(raw.tiles) ? raw.tiles.flatMap(tile) : [],
    limits: limits(raw.thresholds),
  };
}

/** The CHM quality of `object`, read once per object; null while loading, unbound or unreadable. */
export function useChmQuality(object: string | undefined, links: LinkSource): ChmQuality | null {
  const [read, setRead] = useState<{ object: string; quality: ChmQuality | null } | null>(null);
  useEffect(() => {
    if (!object) return;
    let alive = true;
    links
      .url(object, CHM_QUALITY_FILE)
      .then((url) => fetch(url))
      .then((r) => (r.ok ? r.json() : null))
      .then(chmQuality)
      // A file that cannot be read leaves the summary out; the map is the page.
      .catch(() => null)
      .then((quality) => alive && setRead({ object, quality }));
    return () => {
      alive = false;
    };
  }, [object, links]);
  return read && read.object === object ? read.quality : null;
}
